(() => {
  if (customElements.get('zd-model-version-gallery')) return;

  class ModelVersionGallery extends HTMLElement {
    static imageKey(source) {
      if (!source) return '';
      try {
        return decodeURIComponent(new URL(source, location.origin).pathname.split('/').pop());
      } catch {
        return '';
      }
    }

    static versionForVariant(data, variant) {
      if (!variant) return null;
      const versions = data.versions || [];
      return versions.find(version => version.modelUid &&
        String(variant.sku || '').startsWith(`${version.modelUid}-`)) ||
        versions.find(version => String(version.variantId) === String(variant.id)) ||
        versions.find(version => this.imageKey(version.image) && this.imageKey(version.image) ===
          this.imageKey(variant.featured_image?.src || variant.featured_media?.preview_image?.src)) ||
        versions.find(version => (version.value || version.label) ===
          variant.options?.[(data.optionPosition || 3) - 1]) || null;
    }

    static mediaForVersion(version, media) {
      const sources = [version.image, ...(version.images || []).map(image =>
        typeof image === 'string' ? image : image.image || image.url)];
      const keys = new Set(sources.map(source => this.imageKey(source)).filter(Boolean));
      const imageIds = new Set([version.imageId, ...(version.imageIds || [])].filter(Boolean).map(String));
      // REST product-image IDs differ from Liquid media IDs; exact source URLs bridge them.
      return media.filter(item => keys.has(this.imageKey(item.src)) || imageIds.has(String(item.imageId)));
    }

    connectedCallback() {
      if (this.initialized) return;
      try {
        this.data = JSON.parse(this.querySelector('[data-zd-model-data]').textContent);
        this.media = JSON.parse(this.querySelector('[data-zd-media-data]').textContent);
      } catch {
        return;
      }
      this.root = this.closest('section');
      this.picker = this.root?.querySelector('variant-radios, variant-selects');
      this.gallery = this.root?.querySelector('media-gallery');
      if (!this.picker || !this.gallery || !Array.isArray(this.data.versions) || this.data.versions.length < 2) return;
      this.data.optionPosition = Number(this.dataset.optionPosition) || this.data.optionPosition || 3;
      this.initialized = true;
      this.visibility = new Map();
      this.previewParameters = [...new URL(location.href).searchParams].filter(([key]) =>
        ['preview_theme_id', 'preview_key', 'preview_token'].includes(key));
      this.captureChange = event => {
        if (!event.target.matches('input[data-zd-model-value]')) return;
        const select = this.picker.querySelector('[data-zd-model-select]');
        if (select) select.value = event.target.value;
      };
      this.handleChange = event => {
        if (!this.picker.contains(event.target)) return;
        const variant = this.currentVariant();
        const version = ModelVersionGallery.versionForVariant(this.data, variant);
        if (!version) return;
        this.applyVersion(version, variant);
        const url = new URL(location.href);
        for (const [key, value] of this.previewParameters) url.searchParams.set(key, value);
        if (this.previewParameters.length) history.replaceState(history.state, '', url.href);
      };
      this.root.addEventListener('change', this.captureChange, true);
      this.root.addEventListener('change', this.handleChange);
      const variant = this.currentVariant();
      const version = ModelVersionGallery.versionForVariant(this.data, variant);
      if (version) this.applyVersion(version, variant);
    }

    currentVariant() {
      if (this.picker.currentVariant) return this.picker.currentVariant;
      const variants = this.picker.getVariantData?.() || [];
      return variants.find(variant => String(variant.id) === this.dataset.variantId);
    }

    setVisible(node, visible) {
      if (!this.visibility.has(node)) {
        this.visibility.set(node, { hidden: node.hidden, aria: node.getAttribute('aria-hidden') });
      }
      node.hidden = !visible;
      if (visible) {
        node.removeAttribute('data-zd-model-hidden');
        node.removeAttribute('aria-hidden');
      } else {
        node.setAttribute('data-zd-model-hidden', 'true');
        node.setAttribute('aria-hidden', 'true');
      }
    }

    resetSlider(slider) {
      if (!slider) return;
      const track = slider.querySelector('[id^="Slider-"]');
      track?.scrollTo({ left: 0 });
      slider.resetPages?.();
      const visible = [...slider.querySelectorAll('[id^="Slide-"]')].filter(item => !item.hidden);
      if (visible.length < 2) {
        if (slider.currentPageElement) slider.currentPageElement.textContent = '1';
        if (slider.pageTotalElement) slider.pageTotalElement.textContent = '1';
        if (slider.prevButton) slider.prevButton.disabled = true;
        if (slider.nextButton) slider.nextButton.disabled = true;
      }
    }

    applyVersion(version, variant) {
      const media = ModelVersionGallery.mediaForVersion(version, this.media);
      if (!media.length) return;
      const allowed = new Set(media.map(item => String(item.id)));
      const viewer = this.gallery.querySelector('[id^="GalleryViewer"]');
      const thumbnails = this.gallery.querySelector('[id^="GalleryThumbnails"]');
      const items = [...viewer.querySelectorAll('.product__media-item[data-media-id]')];
      const idOf = value => String(value).split('-').pop();
      for (const item of items) this.setVisible(item, allowed.has(idOf(item.dataset.mediaId)));
      for (const item of thumbnails?.querySelectorAll('[data-target]') || []) {
        this.setVisible(item, allowed.has(idOf(item.dataset.target)));
      }
      // Dawn moves product modals into the body, outside the original section.
      const modal = document.getElementById(`ProductModal-${this.root.dataset.section}`)
        ?.querySelector('.product-media-modal__content');
      for (const item of modal?.querySelectorAll('[data-media-id]') || []) {
        this.setVisible(item, allowed.has(String(item.dataset.mediaId)));
      }
      const selected = items.find(item => !item.hidden &&
        idOf(item.dataset.mediaId) === String(variant.featured_media?.id)) ||
        items.find(item => !item.hidden);
      for (const item of items) item.classList.toggle('is-active', item === selected);
      this.resetSlider(viewer);
      this.resetSlider(thumbnails);
      const thumbnail = [...thumbnails?.querySelectorAll('[data-target]') || []].find(item =>
        item.dataset.target === selected?.dataset.mediaId);
      if (thumbnail) this.gallery.setActiveThumbnail?.(thumbnail);
      const value = variant.options?.[(this.data.optionPosition || 3) - 1];
      for (const caption of this.picker.querySelectorAll('[data-zd-model-selection]')) {
        caption.textContent = value || version.value || version.label;
      }
      for (const input of this.picker.querySelectorAll('input[data-zd-model-value]')) {
        input.checked = input.value === value;
      }
      this.root.classList.add('zd-model-version-product-ready');
      this.dataset.activeCode = version.code;
      this.dataset.visibleMediaCount = String(media.length);
    }

    disconnectedCallback() {
      if (!this.initialized) return;
      this.root.removeEventListener('change', this.captureChange, true);
      this.root.removeEventListener('change', this.handleChange);
      this.root.classList.remove('zd-model-version-product-ready');
      for (const [node, before] of this.visibility) {
        node.hidden = before.hidden;
        node.removeAttribute('data-zd-model-hidden');
        if (before.aria === null) node.removeAttribute('aria-hidden');
        else node.setAttribute('aria-hidden', before.aria);
      }
      this.initialized = false;
    }
  }
  customElements.define('zd-model-version-gallery', ModelVersionGallery);
})();
